<script setup lang="ts">
import { applyParticipantVolume } from "~/utils/videoCalls/participantVolume.client";

const props = withDefaults(
  defineProps<{
    stream: MediaStream | null;
    participantId: string;
    volumePercent?: number;
  }>(),
  {
    volumePercent: 100,
  },
);

const emit = defineEmits<{
  "volume-support-change": [supported: boolean];
}>();

const audioElement = ref<HTMLAudioElement | null>(null);
const reportedVolumeSupport = ref<boolean | null>(null);

const normalizedVolume = computed(() => {
  if (!Number.isFinite(props.volumePercent)) return 1;
  return Math.min(100, Math.max(0, props.volumePercent)) / 100;
});

const applyVolume = () => {
  if (!audioElement.value) return;

  const supported = applyParticipantVolume(
    audioElement.value,
    normalizedVolume.value,
  );
  if (
    (normalizedVolume.value !== 1 || !supported) &&
    supported !== reportedVolumeSupport.value
  ) {
    reportedVolumeSupport.value = supported;
    emit("volume-support-change", supported);
  }
};

const attachStream = () => {
  if (audioElement.value && audioElement.value.srcObject !== props.stream) {
    audioElement.value.srcObject = props.stream;
  }
  applyVolume();
};

watch(() => props.stream, attachStream);
watch(normalizedVolume, applyVolume);
onMounted(attachStream);
onBeforeUnmount(() => {
  if (audioElement.value) {
    audioElement.value.srcObject = null;
  }
});
</script>

<template>
  <audio
    ref="audioElement"
    autoplay
    playsinline
    :muted="normalizedVolume === 0"
    class="video-call-remote-audio"
    :data-participant-id="participantId"
    data-testid="video-call-remote-audio"
    @loadedmetadata="applyVolume"
    @playing="applyVolume"
  />
</template>

<style scoped>
.video-call-remote-audio {
  position: fixed;
  width: 1px;
  height: 1px;
  pointer-events: none;
  opacity: 0;
}
</style>
